import React, { useState } from 'react';
import { ChevronDownIcon, TerminalIcon } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';
import type { ToolRendererProps } from './index';

type CocoRelayInput = {
  command:
    | 'echo'
    | 'ping'
    | 'system-info'
    | 'exec'
    | 'file-read'
    | 'file-exists'
    | 'env-get'
    | 'ls'
    | 'grep';
  args?: Record<string, unknown> | null;
  timeout?: number | null;
};

type CocoRelayOutput = {
  success: boolean;
  result?: unknown;
  error?: string;
  cliConnected: boolean;
};

/** Format command + args into a readable CLI-style string. */
function formatCommandLine(input: CocoRelayInput): string {
  const args = input.args ?? {};
  switch (input.command) {
    case 'exec': {
      const profile = args['profile'];
      if (typeof profile === 'string' && profile.length > 0) {
        const prompt =
          args['prompt'] !== undefined ? String(args['prompt']) : '';
        const cwd =
          args['cwd'] !== undefined ? ` (cwd: ${String(args['cwd'])})` : '';
        if (prompt.length > 0) {
          return `profile:${profile} ${prompt}${cwd}`;
        }
        return `profile:${profile}${cwd}`;
      }
      const argv = args['argv'];
      if (Array.isArray(argv)) {
        return argv.map((part) => String(part)).join(' ');
      }
      const cmd = args['cmd'] ?? args['command'] ?? '';
      return `${String(cmd)}`;
    }
    case 'file-read':
    case 'file-exists':
    case 'ls': {
      const path = args['path'] ?? '';
      return `${input.command} ${String(path)}`;
    }
    case 'grep': {
      const parts: string[] = ['grep'];
      if (args['pattern'] !== undefined) {
        parts.push(String(args['pattern']));
      }
      if (args['path'] !== undefined) {
        parts.push(String(args['path']));
      }
      if (args['recursive'] === true) {
        parts.push('-r');
      }
      return parts.join(' ');
    }
    case 'env-get': {
      const name = args['name'] ?? '';
      return `env-get ${String(name)}`;
    }
    case 'echo': {
      const msg =
        args['message'] ?? args['msg'] ?? Object.values(args)[0] ?? '';
      return `echo ${String(msg)}`;
    }
    case 'ping':
    case 'system-info':
      return input.command;
    default:
      return input.command;
  }
}

/** Truncate a stringified value for inline display. */
function truncateOutput(value: unknown, maxLen = 200): string {
  const str =
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  if (str === undefined || str === null) {
    return '';
  }
  if (str.length <= maxLen) {
    return str;
  }
  return str.slice(0, maxLen) + '…';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Extract the human-facing text from a relay command result.
 *
 * CLI-style commands (e.g. `exec`) return `{ stdout, stderr }`; surface the
 * stdout when it has content, otherwise the stderr, mirroring how a real
 * terminal shows command output instead of the raw JSON envelope. Empty
 * output yields an empty string. Other result shapes are passed through as-is
 * (strings) or pretty-printed JSON.
 */
function extractResultText(result: unknown): string | null {
  if (result === undefined || result === null) {
    return null;
  }
  if (typeof result === 'string') {
    return result;
  }
  if (isRecord(result) && ('stdout' in result || 'stderr' in result)) {
    const stdout = typeof result['stdout'] === 'string' ? result['stdout'] : '';
    const stderr = typeof result['stderr'] === 'string' ? result['stderr'] : '';
    if (stdout.trim().length > 0) {
      return stdout;
    }
    if (stderr.trim().length > 0) {
      return stderr;
    }
    return '';
  }
  return JSON.stringify(result, null, 2);
}

const BlinkingCursor: React.FC = () => (
  <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-[blink_1s_step-end_infinite] bg-foreground" />
);

// Keyframes for blinking cursor - injected once via a style tag
const BLINK_STYLE =
  '@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }';

export const RelayToolRenderer: React.FC<ToolRendererProps> = ({ part }) => {
  const running = part.isIncomplete();
  const input = (part.input ?? {}) as CocoRelayInput;
  const output = part.getOutput<CocoRelayOutput>();
  const [expanded, setExpanded] = useState(false);

  const commandLine = formatCommandLine(input);

  const resultText = extractResultText(output?.result);
  const errorText =
    output?.error !== undefined && output.error.trim() !== ''
      ? output.error
      : null;

  // Prefer the command's own output (stdout/stderr); fall back to a
  // top-level relay error only when the command produced nothing.
  const fullOutputText =
    resultText !== null && resultText.length > 0 ? resultText : errorText;

  const outputText =
    fullOutputText !== null && fullOutputText.length > 0
      ? truncateOutput(fullOutputText)
      : null;

  // Only offer expand when there is more to show than the truncated preview.
  const canExpand =
    fullOutputText !== null &&
    outputText !== null &&
    fullOutputText.length > outputText.length;

  const isError = output?.success === false;

  return (
    <>
      <style>{BLINK_STYLE}</style>
      <div className="max-w-[80%] overflow-hidden rounded-lg border border-border bg-card font-mono text-sm mb-4">
        {/* Title bar */}
        <div className="flex items-center gap-3 border-b border-border bg-secondary/80 px-3 py-2">
          <span className="flex-1 text-left text-xs text-muted-foreground select-none">
            {running ? (
              <RunningToolTitle>coco relay</RunningToolTitle>
            ) : (
              'coco relay'
            )}
          </span>
          <TerminalIcon className="h-3.5 w-3.5 text-muted-foreground" />
        </div>

        {/* Terminal body */}
        <div className="px-4 py-3 space-y-1.5">
          {/* Prompt line */}
          <div className="flex items-baseline gap-2">
            <span className="text-success select-none">$</span>
            <span className="text-foreground break-all">{commandLine}</span>
            {running ? <BlinkingCursor /> : null}
          </div>

          {/* Output lines (done state) */}
          {!running && outputText !== null ? (
            <div
              className={cn(
                'mt-1 whitespace-pre-wrap break-all text-xs leading-relaxed',
                isError ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {outputText}
            </div>
          ) : null}

          {/* Expand/collapse for full output — only when truncated */}
          {!running && canExpand ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-1 cursor-pointer focus:outline-none"
            >
              <ChevronDownIcon
                className={cn(
                  'h-3 w-3 transition-transform',
                  expanded ? 'rotate-180' : 'rotate-0'
                )}
              />
              {expanded ? 'Hide full output' : 'Show full output'}
            </button>
          ) : null}

          {expanded && fullOutputText !== null ? (
            <pre
              className={cn(
                'mt-1 max-h-64 overflow-auto rounded bg-muted p-2 text-xs',
                isError ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {fullOutputText}
            </pre>
          ) : null}

          {/* Status indicator */}
          {!running && output !== undefined ? (
            <div
              className={cn(
                'text-xs',
                isError ? 'text-destructive' : 'text-success'
              )}
            >
              {isError ? '✗ failed' : '✓ done'}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};
